import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionApi } from './hooks/useSessionApi';
import { useSessionTimer } from './hooks/useSessionTimer';
import { useWorkoutState } from './hooks/useWorkoutState';
import { useWorkoutProgress } from './hooks/useWorkoutProgress';
import { ReadyPhase } from './components/ReadyPhase';
import { SetupPhase } from './components/SetupPhase';
import { ExercisePhase } from './components/ExercisePhase';
import { RestPhase } from './components/RestPhase';
import { RoundReviewPhase } from './components/RoundReviewPhase';
import { CompletionPhase } from './components/CompletionPhase';
import { FeedbackPhase } from './components/FeedbackPhase';
import { WorkoutProgressBar } from './components/WorkoutProgressBar';
import { estimateTotalDuration } from './utils/estimateDuration';
import type { RecordedSet } from './types';

interface AppProps {
  sessionId: string;
}

export function App({ sessionId }: AppProps) {
  const api = useSessionApi();
  const workout = useWorkoutState();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Refs for stable timer callback — avoids stale closures
  const workoutRef = useRef(workout);
  workoutRef.current = workout;
  const apiRef = useRef(api);
  apiRef.current = api;
  const exerciseDataRef = useRef({ reps: 0, weight: 0 });
  const completingRef = useRef(false);

  // Timer complete handler — reads from refs so it's always current.
  // Empty deps = stable callback, never recreated.
  const handleTimerComplete = useCallback(() => {
    const w = workoutRef.current;
    if (w.phase === 'rest') {
      w.endRest();
    } else if (w.phase === 'round_review') {
      w.startNextRound();
    } else if (w.phase === 'setup') {
      w.setupComplete();
    } else if (w.phase === 'exercise' && !completingRef.current) {
      completingRef.current = true;
      const exercise = w.session?.exercises[w.currentExerciseIndex];
      if (exercise) {
        const { reps, weight } = exerciseDataRef.current;
        const effectiveDuration = (exercise.targetDurationSeconds ?? 0) * (exercise.timeMultiplier ?? 1);
        const set: RecordedSet = {
          sessionExerciseId: exercise.id,
          roundNumber: w.currentRound,
          setNumber: w.currentSetNumber,
          repsCompleted: exercise.exerciseMode === 'RepBased' ? reps : null,
          weightUsedKg: weight > 0 ? weight : null,
          durationCompletedSeconds:
            exercise.exerciseMode === 'TimeBased' ? Math.round(effectiveDuration) : null,
          side: w.currentSide,
          exerciseId: exercise.exerciseId,
          exerciseName: exercise.exerciseName,
          targetReps: exercise.targetReps,
        };
        apiRef.current.recordSet(sessionId, set).catch(() => {});
        w.completeExercise(set);
      }
    }
  }, [sessionId]);

  const handleExerciseDataChange = useCallback((data: { reps: number; weight: number }) => {
    exerciseDataRef.current = data;
  }, []);

  const timer = useSessionTimer({ onComplete: handleTimerComplete });

  const estimatedDuration = workout.session ? estimateTotalDuration(workout.session) : 0;
  const isWorkoutActive = ['setup', 'exercise', 'rest', 'round_review'].includes(workout.phase);
  const workoutProgress = useWorkoutProgress({
    startedAt: workout.startedAt,
    estimatedDurationSeconds: estimatedDuration,
    isActive: isWorkoutActive,
  });

  // Load session on mount — restore position if already in progress
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getSession(sessionId)
      .then((session) => {
        if (cancelled) return;

        // If session was started and has existing progress, restore position
        if (session.startedAt && session.existingSets && session.existingSets.length > 0) {
          workout.restorePosition(session, session.existingSets, session.hasFeedback ?? false);
        } else if (session.startedAt && session.completedAt) {
          // Started + completed but no sets — go to feedback/complete
          workout.restorePosition(session, [], session.hasFeedback ?? false);
        } else {
          // Not started yet — show ready screen
          workout.loadSession(session);
        }
        setLoading(false);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Start rest/round-review/setup timer when entering those phases
  useEffect(() => {
    // Reset completing guard on phase/exercise/set/side transitions
    completingRef.current = false;

    if (workout.phase === 'rest' && workout.session) {
      const duration = workout.restType === 'set'
        ? (workout.session.restBetweenSetsSeconds ?? 60)
        : workout.session.restBetweenExercisesSeconds;
      timer.start(duration);
    } else if (workout.phase === 'round_review' && workout.session) {
      timer.start(workout.session.restBetweenRoundsSeconds);
    } else if (workout.phase === 'setup' && workout.session) {
      timer.start(workout.session.setupTimeSeconds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workout.phase, workout.currentExerciseIndex, workout.currentRound, workout.currentSetNumber, workout.currentSide]);

  // Update URL hash to reflect current phase
  useEffect(() => {
    let hash: string = workout.phase;
    if (workout.phase === 'exercise' || workout.phase === 'setup') {
      hash = `${workout.phase}-${workout.currentExerciseIndex + 1}-round-${workout.currentRound}`;
    } else if (workout.phase === 'rest') {
      hash = `rest-round-${workout.currentRound}`;
    } else if (workout.phase === 'round_review') {
      hash = `round-review-${workout.currentRound}`;
    }
    history.replaceState(null, '', `#${hash}`);
  }, [workout.phase, workout.currentExerciseIndex, workout.currentRound]);

  const handleStart = useCallback(async () => {
    try {
      await api.startSession(sessionId);
      workout.startWorkout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
    }
  }, [api, sessionId, workout]);

  const handleCompleteExercise = useCallback(
    async (set: RecordedSet) => {
      if (completingRef.current) return;
      completingRef.current = true;
      try {
        // Always advance state — don't block workout on API failure
        api.recordSet(sessionId, set).catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to record set');
        });
        workout.completeExercise(set);
      } finally {
        completingRef.current = false;
      }
    },
    [api, sessionId, workout],
  );

  const handleFinishEarly = useCallback(async () => {
    try {
      timer.stop();
      workout.finishWorkout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to finish session');
    }
  }, [timer, workout]);

  const handleSkipRest = useCallback(() => {
    timer.stop();
    workout.endRest();
  }, [timer, workout]);

  const handleSkipRoundRest = useCallback(() => {
    timer.stop();
  }, [timer]);

  const handleStartNextRound = useCallback(
    async (adjustments: Map<string, { reps: number; weight: number }>) => {
      // Store adjustments for next round reference
      for (const [exerciseId, adj] of adjustments) {
        workout.adjustExercise(exerciseId, adj.reps, adj.weight);
      }
      workout.startNextRound();
    },
    [workout],
  );

  const handleCompleteSession = useCallback(async () => {
    try {
      await api.completeSession(sessionId);
    } catch {
      // Session may already be complete — that's ok
    }
  }, [api, sessionId]);

  // Complete session when workout reaches feedback or complete phase
  useEffect(() => {
    if (workout.phase === 'feedback' || workout.phase === 'complete') {
      handleCompleteSession();
    }
  }, [workout.phase, handleCompleteSession]);

  const handleFeedbackSubmit = useCallback(async (feedback: import('./types').FeedbackData) => {
    await api.submitFeedback(sessionId, feedback);
    workout.completeFeedback();
  }, [api, sessionId, workout]);

  const handleFeedbackSkip = useCallback(() => {
    workout.skipFeedback();
  }, [workout]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 p-6">
        <p className="text-body text-amber-600 dark:text-amber-400">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!workout.session) {
    return null;
  }

  const currentExercise = workout.session.exercises[workout.currentExerciseIndex];
  const nextExercise = workout.session.exercises[workout.currentExerciseIndex + 1] ?? null;

  const showProgressBar = isWorkoutActive && workout.phase !== 'exercise';

  const phaseContent = (() => {
    switch (workout.phase) {
      case 'ready':
        return <ReadyPhase session={workout.session} onStart={handleStart} />;

      case 'setup':
        return currentExercise ? (
          <SetupPhase
            exercise={currentExercise}
            roundNumber={workout.currentRound}
            totalRounds={workout.session.rounds}
            currentSide={workout.currentSide}
            timer={timer}
          />
        ) : null;

      case 'exercise':
        return currentExercise ? (
          <ExercisePhase
            exercise={currentExercise}
            nextExercise={nextExercise}
            roundNumber={workout.currentRound}
            totalRounds={workout.session.rounds}
            setNumber={workout.currentSetNumber}
            totalSets={currentExercise.targetSets ?? 1}
            currentSide={workout.currentSide}
            timer={timer}
            availableWeights={workout.session.availableWeights}
            progressPercentage={workoutProgress.percentage}
            onComplete={handleCompleteExercise}
            onFinishEarly={handleFinishEarly}
            onExerciseDataChange={handleExerciseDataChange}
          />
        ) : null;

      case 'rest': {
        const restExercise = workout.restType === 'set' ? currentExercise : nextExercise;
        return restExercise ? (
          <RestPhase
            timer={timer}
            nextExercise={restExercise}
            isNewRound={false}
            restType={workout.restType}
            currentSetNumber={workout.currentSetNumber}
            totalSets={currentExercise?.targetSets ?? 1}
            onSkip={handleSkipRest}
          />
        ) : null;
      }

      case 'round_review':
        return (
          <RoundReviewPhase
            session={workout.session}
            completedRound={workout.currentRound}
            recordedSets={workout.recordedSets}
            timer={timer}
            onSkipRest={handleSkipRoundRest}
            onStartNextRound={handleStartNextRound}
          />
        );

      case 'feedback':
        return (
          <FeedbackPhase
            onSubmit={handleFeedbackSubmit}
            onSkip={handleFeedbackSkip}
          />
        );

      case 'complete':
        return (
          <CompletionPhase
            session={workout.session}
            recordedSets={workout.recordedSets}
            startedAt={workout.startedAt}
          />
        );
    }
  })();

  return (
    <>
      {showProgressBar && <WorkoutProgressBar percentage={workoutProgress.percentage} />}
      {phaseContent}
    </>
  );
}
